import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { PublicPageSection } from '@/layouts/PublicPageLayout';
import type { AboutPortfolioContent } from '@/lib/types/siteSettings';

const ALLIANCE_FEATURES = [
  'High-SRI reflective roof',
  '400+ kW rooftop solar farm',
  'Insulated roof and walls',
  'Storm-water harvesting',
  'Geothermal HVAC',
  'Efficient plumbing fixtures',
  'High-efficiency LED lighting and controls',
  'Geothermal heated parking lots',
  'Bicycle parking and EV-ready spaces',
  'Fibre-optic connectivity',
] as const;

const DOWNTOWN_WEST_TENANTS = [
  'Startups and tech companies',
  'Creative agencies and studios',
  'Fitness and wellness operators',
  'Legal and professional services',
  'Retail and hospitality concepts',
  'Consulting and client-facing teams',
] as const;

const RETAIL_PLAZAS = [
  'Bradstock and Weston properties (Weston Road at Bradstock Ave, between Sheppard Ave and Finch Ave)',
  'Victoria Park Plaza (Victoria Park Ave at St Clair Ave)',
] as const;

interface AboutPortfolioHighlightsSectionProps {
  featuredProperties: string[];
  content?: AboutPortfolioContent | null;
}

const DEFAULT_PORTFOLIO_CONTENT: AboutPortfolioContent = {
  title: 'Portfolio highlights',
  description:
    'A snapshot of our flagship redevelopment and the portfolios we operate across Toronto.',
  ctaLabel: 'View availabilities',
  ctaHref: '/availabilities',
  flagshipEyebrow: 'CleanTech and IT centre',
  flagshipTitle: '501 Alliance Avenue',
  flagshipDescription:
    'A major green renovation of the former Cooper Canada sporting goods factory, near Weston Road and Black Creek, operated through 501 Alliance Investments Inc.',
  flagshipStats: [
    { label: 'Total campus', value: '315,000 sq ft' },
    { label: 'Space ranges', value: '2,000 to 67,000 sq ft' },
    { label: 'Zoning', value: 'Prestige Employment' },
    { label: 'Uses', value: 'Industrial, office, and retail' },
  ],
  flagshipFeaturesHeading: 'Sustainability features',
  flagshipFeaturesToggleLabel: 'See all sustainability features',
  flagshipFeatures: [...ALLIANCE_FEATURES],
  downtownEyebrow: 'Downtown West portfolio',
  downtownTitle: 'Character post-and-beam buildings',
  downtownDescription:
    "Over 130,000 sq ft across four buildings in Toronto's downtown west, with unit sizes from 600 to 7,800 sq ft and surface parking available.",
  downtownIdealHeading: 'Ideal for',
  downtownTenants: [...DOWNTOWN_WEST_TENANTS],
  downtownAddressesHeading: 'Addresses',
  retailEyebrow: 'Retail plazas',
  retailTitle: 'Retail space since 1958',
  retailDescription:
    'Through our parent company S Strashin and Sons Ltd., we have offered retail space across Toronto for decades. Availability changes over time.',
  retailPlazas: [...RETAIL_PLAZAS],
  retailFooter: 'For current retail openings, reach out via the contact details below or visit the',
  retailFooterLinkLabel: 'Contact page',
};

function preferText(value: string | null | undefined, fallback: string): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return fallback;
}

function preferList<T>(value: T[] | null | undefined, fallback: T[]): T[] {
  if (Array.isArray(value) && value.length > 0) {
    return value;
  }
  return fallback;
}

function renderDotList(items: readonly string[]): JSX.Element {
  return (
    <ul className="mt-3 grid gap-2 text-sm text-text-primary/80 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-accent" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function AboutPortfolioHighlightsSection({
  featuredProperties,
  content,
}: AboutPortfolioHighlightsSectionProps): JSX.Element {
  const resolvedContent = {
    title: preferText(content?.title, DEFAULT_PORTFOLIO_CONTENT.title),
    description: preferText(content?.description, DEFAULT_PORTFOLIO_CONTENT.description),
    ctaLabel: preferText(content?.ctaLabel, DEFAULT_PORTFOLIO_CONTENT.ctaLabel),
    ctaHref: preferText(content?.ctaHref, DEFAULT_PORTFOLIO_CONTENT.ctaHref),
    flagshipEyebrow: preferText(
      content?.flagshipEyebrow,
      DEFAULT_PORTFOLIO_CONTENT.flagshipEyebrow,
    ),
    flagshipTitle: preferText(content?.flagshipTitle, DEFAULT_PORTFOLIO_CONTENT.flagshipTitle),
    flagshipDescription: preferText(
      content?.flagshipDescription,
      DEFAULT_PORTFOLIO_CONTENT.flagshipDescription,
    ),
    flagshipStats: preferList(content?.flagshipStats, DEFAULT_PORTFOLIO_CONTENT.flagshipStats),
    flagshipFeaturesHeading: preferText(
      content?.flagshipFeaturesHeading,
      DEFAULT_PORTFOLIO_CONTENT.flagshipFeaturesHeading,
    ),
    flagshipFeaturesToggleLabel: preferText(
      content?.flagshipFeaturesToggleLabel,
      DEFAULT_PORTFOLIO_CONTENT.flagshipFeaturesToggleLabel,
    ),
    flagshipFeatures: preferList(
      content?.flagshipFeatures,
      DEFAULT_PORTFOLIO_CONTENT.flagshipFeatures,
    ),
    downtownEyebrow: preferText(
      content?.downtownEyebrow,
      DEFAULT_PORTFOLIO_CONTENT.downtownEyebrow,
    ),
    downtownTitle: preferText(content?.downtownTitle, DEFAULT_PORTFOLIO_CONTENT.downtownTitle),
    downtownDescription: preferText(
      content?.downtownDescription,
      DEFAULT_PORTFOLIO_CONTENT.downtownDescription,
    ),
    downtownIdealHeading: preferText(
      content?.downtownIdealHeading,
      DEFAULT_PORTFOLIO_CONTENT.downtownIdealHeading,
    ),
    downtownTenants: preferList(
      content?.downtownTenants,
      DEFAULT_PORTFOLIO_CONTENT.downtownTenants,
    ),
    downtownAddressesHeading: preferText(
      content?.downtownAddressesHeading,
      DEFAULT_PORTFOLIO_CONTENT.downtownAddressesHeading,
    ),
    retailEyebrow: preferText(content?.retailEyebrow, DEFAULT_PORTFOLIO_CONTENT.retailEyebrow),
    retailTitle: preferText(content?.retailTitle, DEFAULT_PORTFOLIO_CONTENT.retailTitle),
    retailDescription: preferText(
      content?.retailDescription,
      DEFAULT_PORTFOLIO_CONTENT.retailDescription,
    ),
    retailPlazas: preferList(content?.retailPlazas, DEFAULT_PORTFOLIO_CONTENT.retailPlazas),
    retailFooter: preferText(content?.retailFooter, DEFAULT_PORTFOLIO_CONTENT.retailFooter),
    retailFooterLinkLabel: preferText(
      content?.retailFooterLinkLabel,
      DEFAULT_PORTFOLIO_CONTENT.retailFooterLinkLabel,
    ),
  };
  const flagshipFeaturesPrimary = resolvedContent.flagshipFeatures.slice(0, 6);
  const flagshipFeaturesSecondary = resolvedContent.flagshipFeatures.slice(6);

  return (
    <PublicPageSection tone="base" spacing="lg" aria-label="Portfolio highlights">
      <PublicPageHeading
        title={resolvedContent.title}
        description={resolvedContent.description}
        actions={
          <Link
            to={resolvedContent.ctaHref}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-primary/20 px-5 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
          >
            {resolvedContent.ctaLabel}
          </Link>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {resolvedContent.flagshipEyebrow}
            </p>
            <h3 className="text-2xl font-semibold text-brand-primary">
              {resolvedContent.flagshipTitle}
            </h3>
            <p className="text-sm leading-relaxed text-text-primary/75">
              {resolvedContent.flagshipDescription}
            </p>
          </div>

          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            {resolvedContent.flagshipStats.map((entry) => (
              <div key={entry.label} className="rounded-2xl bg-surface-cream p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {entry.label}
                </dt>
                <dd className="mt-2 text-base font-semibold text-text-primary">{entry.value}</dd>
              </div>
            ))}
          </dl>

          <h4 className="mt-8 text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            {resolvedContent.flagshipFeaturesHeading}
          </h4>
          {renderDotList(flagshipFeaturesPrimary)}
          {flagshipFeaturesSecondary.length > 0 ? (
            <details className="mt-4 rounded-2xl border border-surface-subtle bg-surface-cream/70 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-brand-primary">
                {resolvedContent.flagshipFeaturesToggleLabel}
              </summary>
              <div className="mt-3">{renderDotList(flagshipFeaturesSecondary)}</div>
            </details>
          ) : null}
        </article>

        <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {resolvedContent.downtownEyebrow}
            </p>
            <h3 className="text-2xl font-semibold text-brand-primary">
              {resolvedContent.downtownTitle}
            </h3>
            <p className="text-sm leading-relaxed text-text-primary/75">
              {resolvedContent.downtownDescription}
            </p>
          </div>

          <h4 className="mt-8 text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            {resolvedContent.downtownIdealHeading}
          </h4>
          {renderDotList(resolvedContent.downtownTenants)}

          <h4 className="mt-8 text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            {resolvedContent.downtownAddressesHeading}
          </h4>
          <ul className="mt-3 grid gap-3 text-sm text-text-primary/80 sm:grid-cols-2">
            {featuredProperties.map((property) => (
              <li
                key={property}
                className="rounded-2xl border border-surface-subtle bg-surface-light px-4 py-3"
              >
                {property}
              </li>
            ))}
          </ul>
        </article>

        <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8 lg:col-span-2">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {resolvedContent.retailEyebrow}
            </p>
            <h3 className="text-2xl font-semibold text-brand-primary">
              {resolvedContent.retailTitle}
            </h3>
            <p className="text-sm leading-relaxed text-text-primary/75">
              {resolvedContent.retailDescription}
            </p>
          </div>

          <ul className="mt-6 grid gap-3 text-sm text-text-primary/80 md:grid-cols-2">
            {resolvedContent.retailPlazas.map((plaza) => (
              <li key={plaza} className="rounded-2xl bg-surface-cream p-4">
                {plaza}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-text-primary/70">
            {resolvedContent.retailFooter}{' '}
            <Link to="/contact" className="font-semibold text-brand-primary hover:underline">
              {resolvedContent.retailFooterLinkLabel}
            </Link>
            .
          </p>
        </article>
      </div>
    </PublicPageSection>
  );
}
